
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchWithAuth } from '../apiClient';

// Simple in-memory cache shared across hook instances in this tab.
// Each entry can hold { data, ts, promise } to allow deduping simultaneous fetches.
const activityCache = new Map();

export function useActivityData(originJobKey, toast) {
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Seed from cache on init / originJobKey change
  useEffect(() => {
    if (!originJobKey) return;
    const cached = activityCache.get(originJobKey);
    if (cached && cached.data) {
      setActivityData(cached.data);
      setLoaded(true);
    } else {
      setActivityData(null);
      setLoaded(false);
    }
  }, [originJobKey]);

  const fetchActivityData = useCallback(async (opts) => {
    // opts: { force?: boolean }
    if (!originJobKey) return;
    const force = opts && opts.force;

    const cached = activityCache.get(originJobKey);
    if (!force && cached && cached.data) {
      // already cached
      setActivityData(cached.data);
      setLoaded(true);
      return cached.data;
    }

    if (!force && cached && cached.promise) {
      // another instance is already fetching — reuse the promise
      setLoading(true);
      try {
        const data = await cached.promise;
        setActivityData(data);
        setLoaded(true);
        return data;
      } catch (err) {
        // fallthrough to error handling below
        throw err;
      } finally {
        setLoading(false);
      }
    }

    // If we get here, start a fetch and store the promise for others.
    const p = (async () => {
      try {
        const response = await fetchWithAuth(`/api/activity/${originJobKey}`);
        if (!response.ok) {
          throw new Error('Failed to fetch activity data');
        }
        const data = await response.json();
        // persist into cache
        activityCache.set(originJobKey, { data, ts: Date.now() });
        return data;
      } catch (error) {
        // on error, clear promise so future attempts can retry
        const cur = activityCache.get(originJobKey) || {};
        delete cur.promise;
        activityCache.set(originJobKey, cur);
        throw error;
      }
    })();

    // store promise
    activityCache.set(originJobKey, { ...(activityCache.get(originJobKey) || {}), promise: p });

    setLoading(true);
    try {
      const data = await p;
      setActivityData(data);
      setLoaded(true);
      return data;
    } catch (error) {
      toast && toast({
        title: 'Error fetching activity data.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return null;
    } finally {
      setLoading(false);
      // remove promise from cache since resolved or failed
      const cur = activityCache.get(originJobKey) || {};
      delete cur.promise;
      activityCache.set(originJobKey, cur);
    }
  }, [originJobKey, toast]);

  // Memoized processed activity data and counts
  const processedActivityData = useMemo(() => {
    if (!activityData || activityData.length === 0) {
      return {
        sortedActivities: [],
        initialIndex: -1,
        consecutiveRetryCount: 0,
        latestActivity: null,
        latestControlActivity: null,
        isPaused: false,
      };
    }

    // Sort activities by firedTime descending
    const sortedActivities = [...activityData].sort(
      (a, b) => new Date(b.firedTime) - new Date(a.firedTime)
    );

    const latestActivity = sortedActivities[0] ?? null;
    const controlActionSet = new Set(['complete', 'pause', 'resume']);
    const latestControlActivity = sortedActivities.find((activity) => {
      const action = activity.action?.toLowerCase();
      return action && controlActionSet.has(action);
    }) || null;

    // Find the index of the initial firing activity (type or status indicating initial)
    const initialIndex = sortedActivities.findIndex(
      (activity) =>
        activity.type?.toLowerCase().includes('initial') ||
        activity.status?.toLowerCase().includes('initial')
    );

    // Count retry activities after the initial firing only
    let consecutiveRetryCount = 0;
    if (initialIndex === -1) {
      for (let activity of sortedActivities) {
        if (activity.retryCount != null && activity.retryCount !== '') {
          consecutiveRetryCount++;
        } else {
          break;
        }
      }
      consecutiveRetryCount = consecutiveRetryCount > 0 ? consecutiveRetryCount - 1 : 0;
    } else {
      for (let i = 0; i < initialIndex; i++) {
        const activity = sortedActivities[i];
        if (activity.retryCount != null && activity.retryCount !== '') {
          consecutiveRetryCount++;
        } else {
          break;
        }
      }
    }

    const controlStatus = latestControlActivity?.status?.toLowerCase();
    const controlAction = latestControlActivity?.action?.toLowerCase();
    const isPaused = controlStatus === 'paused' || controlAction === 'pause';

    return {
      sortedActivities,
      initialIndex,
      consecutiveRetryCount,
      latestActivity,
      latestControlActivity,
      isPaused,
    };
  }, [activityData]);

  return {
    activityData: processedActivityData.sortedActivities,
    loading,
    loaded,
    fetchActivityData,
    getConsecutiveRetryCount: () => processedActivityData.consecutiveRetryCount,
    latestActivity: processedActivityData.latestActivity,
    latestControlActivity: processedActivityData.latestControlActivity,
    isPaused: processedActivityData.isPaused,
  };
}
